<script>
import Layout from '@/views/layouts/main'
import PageHeader from '@/components/page-header'
import fiscalContext from '@/modules/nfse/application/nfsePageContext'
import { fiscalStatusLabel, formatFiscalMoney } from '@/modules/nfse/application/nfseWorkflowContract'

const emptyFilters = () => ({ date_ini: '', date_end: '', agreement_id: '', professional_id: '', pacient_id: '', specialty_id: '', nfse_generated: 'all', page: 1, per_page: 25 })
export default {
  name: 'EvolutionBillingPage',
  page: { title: 'Faturamento de evoluções' },
  components: { Layout, PageHeader },
  mixins: [fiscalContext],
  data() { return { filters: emptyFilters(), agreements: [], supportMessage: '' } },
  computed: {
    state() { return this.flow.state },
    pages() { return Math.max(1, Math.ceil(this.state.total / this.filters.per_page)) },
    pageSelected() { const items = this.state.items.filter(item => this.flow.selectable(item)); return items.length > 0 && items.every(item => this.state.selectedIds.includes(Number(item.id))) },
  },
  mounted() { this.loadAgreements() },
  methods: {
    statusLabel: fiscalStatusLabel,
    money: formatFiscalMoney,
    async loadAgreements() {
      if (!this.canFiscal('faturamento.evolucoes.view')) return
      const generation = this.flow.generation
      const result = await this.flow.controller.agreements()
      if (generation !== this.flow.generation) return
      this.agreements = result.success ? result.data : []
      this.supportMessage = result.success ? '' : result.message
    },
    resetFiscalPage() {
      this.filters = emptyFilters()
      this.agreements = []
      this.supportMessage = ''
      this.loadAgreements()
    },
    async search(page = 1) {
      if (!this.canFiscal('faturamento.evolucoes.view')) return
      this.filters.page = page
      await this.flow.search(this.filters)
    },
    filtersChanged() {
      // Não preparar seleção antiga enquanto a UI já exibe filtros diferentes.
      this.flow.queryRevision++
      this.state.selectedIds = []
      this.state.items = []
      this.state.filters = null
      this.state.total = 0
      this.state.totalValue = null
      this.flow.invalidatePreview()
    },
    selectPage(checked) { this.state.items.forEach(item => this.flow.select(item, checked)) },
    async prepare() {
      if (!this.canFiscal('nfse.preview')) return
      const operation = await this.flow.prepareContext()
      if (operation) this.$router.push({ name: 'nfse', query: { operation_id: operation.operation_id } })
    },
  },
}
</script>

<template>
  <Layout>
    <PageHeader title="Faturamento de evoluções" :items="[{ text: 'Financeiro' }, { text: 'Evoluções', active: true }]" />
    <div class="card"><div class="card-body">
      <div class="d-flex flex-wrap justify-content-between align-items-start mb-3">
        <div><h1 class="h4">Consultar e selecionar evoluções</h1><p class="text-muted">O total e a elegibilidade são apurados pelo servidor. A emissão de NFS-e não cria ContaReceber.</p></div>
        <router-link v-if="canFiscal('nfse.preview') || canFiscal('nfse.documents.view')" class="btn btn-outline-primary" to="/financeiro/nfse">Emissão manual e documentos</router-link>
      </div>
      <p v-if="!canFiscal('faturamento.evolucoes.view')" role="alert">Sem permissão para consultar evoluções.</p>
      <form v-else @submit.prevent="search(1)">
        <fieldset :disabled="state.busy">
          <legend class="h6">Filtros da consulta</legend>
          <div class="row">
            <label class="col-md-3 mb-3">Data inicial *<input v-model="filters.date_ini" class="form-control" type="date" required @change="filtersChanged" /></label>
            <label class="col-md-3 mb-3">Data final *<input v-model="filters.date_end" class="form-control" type="date" required @change="filtersChanged" /></label>
            <label class="col-md-4 mb-3">Convênio *<select v-model="filters.agreement_id" class="form-control" required @change="filtersChanged"><option value="">Selecione</option><option v-for="agreement in agreements" :key="agreement.id" :value="agreement.id">{{ agreement.name }}</option></select></label>
            <label class="col-md-2 mb-3">NFS-e gerada<select v-model="filters.nfse_generated" class="form-control" @change="filtersChanged"><option value="all">Todos</option><option value="yes">Sim</option><option value="no">Não</option></select></label>
            <label class="col-md-3 mb-3">ID do profissional<input v-model="filters.professional_id" class="form-control" type="number" min="1" @input="filtersChanged" /></label>
            <label class="col-md-3 mb-3">ID do paciente<input v-model="filters.pacient_id" class="form-control" type="number" min="1" @input="filtersChanged" /></label>
            <label class="col-md-3 mb-3">ID da especialidade<input v-model="filters.specialty_id" class="form-control" type="number" min="1" @input="filtersChanged" /></label>
            <label class="col-md-3 mb-3">Itens por página<select v-model.number="filters.per_page" class="form-control" @change="filtersChanged"><option :value="25">25</option><option :value="50">50</option></select></label>
          </div>
          <button class="btn btn-primary" type="submit">{{ state.busy ? 'Consultando…' : 'Consultar evoluções' }}</button>
        </fieldset>
      </form>
      <p v-if="supportMessage" class="alert alert-warning mt-3" role="alert">{{ supportMessage }}</p>
      <p v-if="state.message" class="alert alert-warning mt-3" role="alert">{{ state.message }}</p>
      <div class="d-flex flex-wrap justify-content-between my-3">
        <span>{{ state.total }} registros · Total da consulta: <strong>{{ money(state.totalValue) }}</strong></span>
        <span>{{ state.selectedIds.length }} selecionados entre páginas. O contexto recalculará o total da seleção.</span>
      </div>
      <div class="table-responsive">
        <table class="table table-hover">
          <caption>Elegibilidade fiscal informada pelo servidor. Vínculo financeiro não comprova emissão.</caption>
          <thead><tr><th><input type="checkbox" aria-label="Selecionar elegíveis desta página" :checked="pageSelected" :disabled="state.busy || !state.items.length" @change="selectPage($event.target.checked)" /></th><th>Evolução</th><th>Data</th><th>Valor</th><th>Código / origem</th><th>Situação fiscal</th><th>Elegibilidade</th></tr></thead>
          <tbody>
            <tr v-for="item in state.items" :key="item.id">
              <td><input type="checkbox" :aria-label="'Selecionar evolução ' + item.id" :disabled="state.busy || !flow.selectable(item)" :checked="state.selectedIds.includes(Number(item.id))" @change="flow.select(item, $event.target.checked)" /></td>
              <td>{{ item.id }}</td><td>{{ item.date || item.start || '—' }}</td><td>{{ money(item.value) }}</td><td>{{ item.code || 'Não informado' }} / {{ item.code_origin || 'unknown' }}</td><td>{{ statusLabel(item.fiscal_status) }}</td><td>{{ item.eligible === true ? 'Elegível' : (item.blocked_reason || 'Indisponível para emissão') }}</td>
            </tr>
            <tr v-if="!state.items.length"><td colspan="7" class="text-center text-muted">{{ state.busy ? 'Carregando…' : 'Nenhuma evolução carregada. Informe os filtros e consulte.' }}</td></tr>
          </tbody>
        </table>
      </div>
      <div class="d-flex flex-wrap justify-content-between align-items-center">
        <nav aria-label="Paginação das evoluções"><button class="btn btn-outline-secondary mr-2" :disabled="state.busy || filters.page <= 1" @click="search(filters.page - 1)">Anterior</button><span>Página {{ filters.page }} de {{ pages }}</span><button class="btn btn-outline-secondary ml-2" :disabled="state.busy || filters.page >= pages" @click="search(filters.page + 1)">Próxima</button></nav>
        <button v-if="canFiscal('nfse.preview')" class="btn btn-primary" :disabled="state.busy || !state.selectedIds.length" @click="prepare">Preparar NFS-e da seleção</button>
      </div>
    </div></div>
  </Layout>
</template>
